import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';

import { Badge } from '@/components/Badge/Badge';
import { type CallToday, type ClientHeatmapReport } from '@/features/reports/api';
import { HEATMAP_COPY } from '@/features/reports/copy';
import { HeatGrid, Legend } from '@/features/reports/heatmap/HeatGrid';
import { signalVariant, weekRange } from '@/features/reports/heatmap/labels';
import { ReportCard } from '@/features/reports/parts';

const COPY = HEATMAP_COPY.engagement;

/**
 * Client engagement (ALS-88): one row per client, one column per week the log holds, each cell her
 * lookbook pages opened, wardrobe views and orders opened, and her signal for the window. A row
 * opens the client; Call today names the one whose score fell the most and opens her too.
 */
export function EngagementCard({ report }: { report: ClientHeatmapReport }) {
    const range = weekRange(report);
    return (
        <ReportCard
            title={COPY.title}
            subtitle={COPY.subtitle(range)}
            actions={report.callToday ? <CallTodayLink call={report.callToday} /> : undefined}
        >
            {report.engagement.length === 0 ? (
                <p className="text-muted-foreground py-4 text-sm">{COPY.empty}</p>
            ) : (
                <>
                    <HeatGrid
                        label={COPY.title}
                        rowHead={COPY.client}
                        columns={report.columns.map((c) => c.label)}
                        tone="heat"
                        cellLabel={COPY.cell}
                        emptyLabel={COPY.noActivity}
                        trailingHead={COPY.signal}
                        openable
                        rows={report.engagement.map((row) => ({
                            key: String(row.customerId),
                            name: row.name,
                            header: (
                                <Link
                                    to="/customers/$customerId"
                                    params={{ customerId: String(row.customerId) }}
                                    className="pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                                >
                                    {row.name}
                                </Link>
                            ),
                            scores: row.scores,
                            trailing: (
                                <Badge size="sm" variant={signalVariant(row.signal)}>
                                    {COPY.signals[row.signal]}
                                </Badge>
                            ),
                        }))}
                    />
                    <Legend tone="heat" low={COPY.legend.low} high={COPY.legend.high} />
                </>
            )}
        </ReportCard>
    );
}

/**
 * The one thing on the screen that asks for a decision, so it is a control rather than a caption:
 * it opens the client it names, which is what a stylist would do next anyway.
 */
function CallTodayLink({ call }: { call: CallToday }) {
    return (
        <Link
            to="/customers/$customerId"
            params={{ customerId: String(call.customerId) }}
            className="border-warning/40 bg-warning/10 text-warning hover:border-warning hover:bg-warning/20 inline-flex min-h-8 items-center gap-2 rounded-full border px-3 py-1.5 text-xs whitespace-nowrap transition-colors pointer-coarse:min-h-11"
        >
            <span>
                {COPY.call} · <strong className="font-semibold">{call.name}</strong>
            </span>
            <ArrowRight aria-hidden className="size-3.5" />
        </Link>
    );
}
